/**
 * Explore content queries (workouts, exercises, mindfulness, recipes)
 * Replaces ExploreContentBuilder and ExploreEntryBuilder static methods
 */
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useBackend } from './useBackend';
import { buildExploreEntry } from '../Model/Explore/ExploreEntryBuilder';
import { createRecipe, Recipe } from '../Model/Diet/Recipe/Recipe';
import { ExploreEntry, ExploreEntryUnion } from '../Model/Explore/ExploreEntry';
import { createExploreCategory, ExploreCategory } from '../Model/Explore/ExploreCategory';
import { v1ParamsToV3Params } from '../Model/Explore/exploreV3Params';
import { getAllExploreCategoriesV3 } from '../Model/Explore/exploreCategoryTreeV3';

/**
 * Factory function to build either Recipe or ExploreEntry from JSON
 * Explore endpoints can return both types
 */
function buildExploreContent(json: any): Recipe | ExploreEntry {
  if (json.type === 'recipe') {
    return createRecipe(json);
  }
  return buildExploreEntry(json);
}

// Single Explore Content (can be Recipe or ExploreEntry)
export function useExploreContent(id?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['explore-content', id],
    queryFn: async () => {
      if (!id) return undefined;
      // V3 splits entries and categories; try the entry endpoint first, then the
      // category endpoint (recipes are fetched via the recipe API, not here).
      const entry = await backend.get(`/coach/explore/v3/entries/${id}`).catch(() => undefined);
      if (entry) return buildExploreEntry(entry);
      const category = await backend.get(`/coach/explore/v3/categories/${id}`).catch(() => undefined);
      return category ? createExploreCategory(category) : undefined;
    },
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Explore Content Search (returns array of Recipe | ExploreEntry)
export function useExploreContentSearch(params: {
  rootCategories?: string[];
  tags?: string[];
  page?: number;
  size?: number;
}) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['explore-content-search', params],
    queryFn: async () => {
      const json = await backend.get('/coach/explore/v3/entries', v1ParamsToV3Params(params));
      return (json ?? []).map((item: any) => buildExploreContent(item));
    },
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
  });
}

// Recipe by Slug (specific recipe lookup)
export function useRecipeBySlug(slug?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['recipe-slug', slug],
    queryFn: async () => {
      if (!slug) return undefined;
      // Recipes live on the dedicated recipe service, not the explore graph.
      const json = await backend.get(`/coach-program/diet/recipes/${slug}`).catch(() => undefined);
      return json ? createRecipe(json) : undefined;
    },
    enabled: !!slug,
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
}

// Random Recipes
export function useRandomRecipes(sampleSize: number = 8) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['recipes-random', sampleSize],
    queryFn: async () => {
      // Recipes live on the dedicated recipe service, not the explore graph.
      const json = await backend.get('/coach-program/diet/recipes/random', { sampleSize });
      return (json ?? []).map((r: any) => createRecipe(r));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - random content can refresh
  });
}

// Explore Entries by categories (for related content)
export function useExploreEntriesQuery(
  params: { categories?: string[]; size?: number; page?: number },
  options?: { enabled?: boolean },
) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['explore-entries', params],
    queryFn: async () => {
      const json = await backend.get(
        '/coach/explore/v3/entries',
        v1ParamsToV3Params({
          categories: params.categories,
          size: params.size,
          page: params.page,
          contentType: 'entry',
        }),
      );
      return (json ?? []).map((item: any) => buildExploreEntry(item));
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

// Explore Category by Identifier
export function useExploreCategoryByIdentifier(identifier?: string) {
  return useQuery({
    queryKey: ['explore-category-identifier', identifier],
    queryFn: async () => {
      if (!identifier) return undefined;
      // V3 has no slug lookup — resolve from the flat category set by id or slug.
      const all = await getAllExploreCategoriesV3();
      return all.find((c) => c.id === identifier || c.identifier === identifier);
    },
    enabled: !!identifier,
    staleTime: 10 * 60 * 1000,
  });
}
